import { Dtos } from "@inrev/common";
import type { FollowedEntityIds } from "@portal/api/followers";
import { DateTime } from "luxon";
import { buildTrend, type MetricTrend, toMs } from "../../../shared/dashboard/metrics/buildTrend";
import type { EntityScope } from "../../../shared/scope";

type AccountPreview = Pick<
	Dtos.SuretyAccount.Preview.Get.Response,
	"id" | "hasBeenActive" | "status"
>;
type BondPreview = Pick<
	Dtos.SuretyBond.Preview.Get.Response,
	"id" | "premium" | "agencyCommission" | "effectiveDate"
>;
type BondRequestPreview = Pick<Dtos.SuretyQuote.Preview.Get.Response, "id" | "status">;

export type AgentDashboardMetricsInput = {
	scope: EntityScope;
	followed: FollowedEntityIds;
	accountPreviews: AccountPreview[];
	bondPreviews: BondPreview[];
	bondRequestPreviews: BondRequestPreview[];
	/** Injectable "now" for deterministic trend windows in tests. */
	nowMs?: number;
};

export type AgentDashboardMetrics = {
	openSubmissions: number;
	openBondRequests: number;
	activeAccounts: number;
	trends: {
		trailing90DayGwp: MetricTrend;
		trailing90DayCommission: MetricTrend;
	};
};

const TRAILING_WINDOW_DAYS = 90;
const TRAILING_SERIES_BUCKETS = 13;

export const computeAgentMetrics = ({
	scope,
	followed,
	accountPreviews,
	bondPreviews,
	bondRequestPreviews,
	nowMs,
}: AgentDashboardMetricsInput): AgentDashboardMetrics => {
	const now = nowMs ?? DateTime.now().toMillis();
	const includeAll = scope === "everyone";

	const scopedAccounts = includeAll
		? accountPreviews
		: accountPreviews.filter((account) => followed.accountIds.has(account.id));
	const scopedBondRequests = includeAll
		? bondRequestPreviews
		: bondRequestPreviews.filter((request) => followed.quoteIds.has(request.id));
	const scopedBonds = includeAll
		? bondPreviews
		: bondPreviews.filter((bond) => followed.bondIds.has(bond.id));

	const openSubmissions = scopedAccounts.filter(
		(account) =>
			!account.hasBeenActive && (account.status === "review" || account.status === "approved"),
	).length;

	const openBondRequests = scopedBondRequests.filter(
		(request) =>
			request.status === "accepted" ||
			request.status === "approved" ||
			request.status === "pending" ||
			request.status === "review",
	).length;

	const activeAccounts = scopedAccounts.filter((account) => account.status === "active").length;

	const trailingTrend = (weightOf: (bond: BondPreview) => number): MetricTrend =>
		buildTrend(
			scopedBonds.map((bond) => ({ atMs: toMs(bond.effectiveDate), weight: weightOf(bond) })),
			now,
			{ windowDays: TRAILING_WINDOW_DAYS, buckets: TRAILING_SERIES_BUCKETS, currency: true },
		);

	return {
		openSubmissions,
		openBondRequests,
		activeAccounts,
		trends: {
			trailing90DayGwp: trailingTrend((bond) => bond.premium),
			trailing90DayCommission: trailingTrend((bond) => bond.agencyCommission),
		},
	};
};
